import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loading } from "../../componentes/loading/loading";
import { useProcessos } from "../../hooks/solicitacoes/useProcessos";
import { useEncaminhamento } from "../../hooks/solicitacoes/useEncaminhamento";
import { useRascunho } from "../../hooks/solicitacoes/useRascunho";
import { useAuth } from "../../context/authContext";
import EncaminhamentoSidebar from "../../components/solicitacoes/Shared/EncaminhamentoSidebar";
import AcaoConcluidaScreen from "../../components/solicitacoes/Shared/AcaoConcluidaScreen";
import EnvioProgressoOverlay from "../../components/solicitacoes/Shared/EnvioProgressoOverlay";
import { RascunhoAviso, RascunhoBotao } from "../../components/solicitacoes/Rascunho/RascunhoBarra";
import { RascunhoContext } from "../../components/solicitacoes/Rascunho/RascunhoContext";
import { atualizarProgressoEnvio, finalizarProgressoEnvio, iniciarProgressoEnvio } from "../../utils/solicitacoes/progressoEnvio";
import { departamentoInfo } from "../../utils/solicitacoes/processoRegistry";

export default function NovaSolicitacao() {
    const navigate = useNavigate();
    const location = useLocation();
    const { departamentos, loading } = useProcessos();
    const [departamentosAbertos, setDepartamentosAbertos] = useState(new Set());
    const [tipoProcesso, setTipoProcesso] = useState(null);
    const [solicitacaoCriada, setSolicitacaoCriada] = useState(null);
    const encaminhamento = useEncaminhamento();
    const { user } = useAuth();
    const rascunho = useRascunho(user?.id, tipoProcesso);

    // Deep-link vindo de outra tela (ex.: Despesas Recorrentes) já com o processo escolhido e os
    // campos pré-preenchidos — `location.state` é consumido só uma vez, no primeiro render.
    const prefillInicial = location.state?.prefill || null;

    useEffect(() => {
        document.title = "MyDash | Nova Solicitação";
    }, []);

    useEffect(() => {
        if (location.state?.tipoProcesso) {
            setTipoProcesso(location.state.tipoProcesso);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Árvore começa toda recolhida (departamentosAbertos vazio no useState) — o usuário abre só o
    // departamento que interessa.

    // Carregamento em tela cheia durante o envio (EnvioProgressoOverlay): começa quando o formulário
    // chama service.criarSolicitacao, os anexos atualizam o progresso (anexosStaged.js) e termina no
    // onCriada — assim nenhum dos formulários de criação precisa controlar o overlay.
    const processoAtual = departamentos.flatMap(d => d.processos).find(p => p.tipoProcesso === tipoProcesso) || null;
    const serviceComProgresso = useMemo(() => {
        const service = processoAtual?.service;
        if (!service) return service;
        const embrulhado = Object.create(service);
        embrulhado.criarSolicitacao = (...args) => {
            iniciarProgressoEnvio("Criando solicitação...");
            return service.criarSolicitacao(...args)
                .then(res => {
                    atualizarProgressoEnvio({ titulo: `Solicitação Nº ${res.data?.numero ?? ""} criada!`, subtitulo: "Concluindo..." });
                    return res;
                })
                .catch(erro => {
                    finalizarProgressoEnvio();
                    throw erro;
                });
        };
        return embrulhado;
    }, [processoAtual]);

    // Saiu da página no meio (ou algo interrompeu antes do onCriada) — não deixa o overlay preso.
    useEffect(() => finalizarProgressoEnvio, []);

    function aoCriar(solicitacao) {
        finalizarProgressoEnvio();
        rascunho.limparAposCriar();
        setSolicitacaoCriada(solicitacao);
    }

    if (solicitacaoCriada) {
        return (
            <AcaoConcluidaScreen
                acao="CRIAR"
                etapaAnterior={null}
                solicitacao={solicitacaoCriada}
                onVerSolicitacao={() => navigate(`/solicitacoes/${solicitacaoCriada.id}`)}
            />
        );
    }

    // Mesma ideia da tela de Detalhe: os campos da etapa 1 já validaram, aqui só resta perguntar
    // (via Sidebar) quem recebe a etapa 2 antes de realmente criar a solicitação.
    function encaminhar(etapaDestinoCodigo, contextoCondicao) {
        return encaminhamento.abrirParaCriacao(tipoProcesso, "ETAPA_1_SOLICITACAO", etapaDestinoCodigo, contextoCondicao);
    }

    function toggleDepartamento(departamento) {
        setDepartamentosAbertos(prev => {
            const proximo = new Set(prev);
            if (proximo.has(departamento)) proximo.delete(departamento);
            else proximo.add(departamento);
            return proximo;
        });
    }

    const processoSelecionado = processoAtual;
    const FormularioCriacao = processoSelecionado?.formularioCriacao;

    let subtitulo = "Escolha o processo";
    if (processoSelecionado) subtitulo = "Preencha os dados da solicitação";

    return (
        <main className="px-4 py-6">
            <div className="mx-auto flex flex-col gap-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h1 className="font-display text-2xl font-bold text-foreground">
                            {!processoSelecionado?.label ?
                                "Iniciar Solicitações"
                                : processoSelecionado.label
                            }
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">{subtitulo}</p>
                    </div>
                    {processoSelecionado && FormularioCriacao && (
                        <RascunhoBotao ultimoSalvamento={rascunho.ultimoSalvamento} onSalvar={rascunho.salvar} />
                    )}
                </div>

                <div>
                    {loading && <Loading label="Carregando processos..." />}

                    {!loading && !processoSelecionado && (
                        <div className="flex flex-col gap-6 bg-white p-3 px-4 rounded-xl border border-border">
                            {departamentos.map(d => {
                                const aberto = departamentosAbertos.has(d.departamento);
                                const info = departamentoInfo(d.departamento);
                                return (
                                    <div key={d.departamento} className="flex flex-col gap-3">
                                        <button
                                            type="button"
                                            onClick={() => toggleDepartamento(d.departamento)}
                                            className="w-full flex items-center gap-3 text-left group"
                                        >
                                            <div className="p-2.5 px-3 rounded-full bg-primary/10 text-primary shrink-0">
                                                <i className={`${info.icone} text-center text-sm w-4 h-4`} />
                                            </div>
                                            <span className="font-display text-base font-semibold text-foreground flex-1 group-hover:text-primary transition-colors duration-150">
                                                {info.label}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {d.processos.length} {d.processos.length > 1 ? "processos" : "processo"}
                                            </span>
                                            <i className={`fa fa-chevron-down text-muted-foreground text-xs transition-transform duration-200 ${aberto ? "rotate-180" : ""}`} />
                                        </button>

                                        {aberto && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                                {d.processos.map(p => (
                                                    <button
                                                        key={p.tipoProcesso}
                                                        type="button"
                                                        onClick={() => setTipoProcesso(p.tipoProcesso)}
                                                        className="flex items-center gap-3 p-3.5 bg-card border border-border rounded-lg shadow-card hover:shadow-soft hover:-translate-y-0.5 hover:border-primary/40 transition-all duration-150 text-left"
                                                    >
                                                        <div className="p-2.5 px-3 rounded-full bg-primary/10 text-primary shrink-0">
                                                            <i className={`${p.icone} text-sm`} />
                                                        </div>
                                                        <span className="text-sm font-medium text-foreground">{p.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {processoSelecionado && FormularioCriacao && (
                        <div className="flex flex-col gap-4">
                            <button
                                type="button"
                                onClick={() => setTipoProcesso(null)}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors duration-150 w-fit"
                            >
                                <i className="fa fa-arrow-left" /> Voltar aos processos
                            </button>
                            <RascunhoAviso
                                rascunhoPendente={rascunho.rascunhoPendente}
                                onRestaurar={rascunho.restaurar}
                                onDescartar={rascunho.descartar}
                            />
                            <RascunhoContext.Provider value={rascunho.contexto}>
                                <FormularioCriacao
                                    service={serviceComProgresso}
                                    etapaInicialExigeAnexo={processoSelecionado.etapaInicialExigeAnexo}
                                    onCriada={aoCriar}
                                    encaminhar={encaminhar}
                                    initialData={prefillInicial}
                                />
                            </RascunhoContext.Provider>
                        </div>
                    )}
                </div>
            </div>

            <EnvioProgressoOverlay />

            <EncaminhamentoSidebar
                isOpen={encaminhamento.aberto}
                carregando={encaminhamento.carregando}
                preview={encaminhamento.preview}
                contexto={encaminhamento.contexto}
                onCancelar={encaminhamento.cancelar}
                onConfirmar={encaminhamento.confirmar}
            />
        </main>
    );
}
